import { Component, Input } from '@angular/core';
import { Params } from '@angular/router';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import {
  CustomerMembership,
  CustomerMembershipCustomer,
  getMembershipDurationDisplay,
  getMembershipIntervalDisplay,
  getMembershipPurchaseTypeDisplay,
  MembershipPerCustomerAddOn,
  MembershipTermsAndConditionsReason,
} from '../../../../../../../../../models/membership';
import {
  CustomerMembershipStatus,
  MembershipDiscountType,
  MembershipPurchaseType,
} from '../../../../../../../../../models/membership-enums';
import { RESERVATION_DETAILS_QUERY_PARAM } from '../../../../../../events/modals/details-modal-query-params';
import { dateStringFromISO8601, propExists } from '../../../../../../../../../utils';
import { MarkdownRenderService } from '../../../../../../../../../services/markdown-render/markdown-render.service';
import { OrganizationConfigService } from '../../../../../../../../../services/organization-config/organization-config.service';

// The query parameter used to deep-link a membership details modal. When this
// is present in the URL, the memberships sub-list / roster report opens the
// corresponding modal on load, so the modal can be shared via a link.
export const MEMBERSHIP_DETAILS_QUERY_PARAM = 'membershipDetailsId';

// Terms & conditions longer than this (characters) are truncated behind a
// "View More" toggle, per PRO-1245.
const TERMS_TRUNCATE_LENGTH = 500;

interface MembershipDetailsMember {
  id: number;
  name: string;
  // True for members who have been added but haven't linked their account yet.
  isUnlinked: boolean;
  // Extra per-member lines (add-ons, freeze state, etc.), each ready to render.
  details: string[];
}

@Component({
  selector: 'app-membership-details-modal',
  templateUrl: './membership-details-modal.component.html',
  styleUrls: ['./membership-details-modal.component.css'],
})
export class MembershipDetailsModalComponent {

  @Input() membership: CustomerMembership;
  // Opens the manage/edit flow for this membership (supplied by the opener so
  // the same edit modal + post-save behaviour is reused). When omitted, the
  // footer Manage button is hidden.
  @Input() manageCallback: () => void;

  public showFullTerms = false;

  public get reservationQueryParams(): Params {
    return {
      [RESERVATION_DETAILS_QUERY_PARAM]:
        this.membership.event_granting_benefit_reservation_id
    };
  }

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly getMembershipPurchaseTypeDisplay =
    getMembershipPurchaseTypeDisplay;
  public readonly MembershipTermsAndConditionsReason =
    MembershipTermsAndConditionsReason;
  public readonly MembershipDiscountType = MembershipDiscountType;
  public readonly MembershipPurchaseType = MembershipPurchaseType;

  constructor(
    public activeModal: NgbActiveModal,
    public orgConfig: OrganizationConfigService,
    public markdownRender: MarkdownRenderService,
  ) {}

  // The membership's billing interval (recurring) or duration (prepaid),
  // mirroring the "Length" column in the memberships sub-list.
  public get lengthDisplay(): string {
    const membership = this.membership;
    switch (membership.purchase_type) {
      case MembershipPurchaseType.recurring:
        return membership.interval
          ? getMembershipIntervalDisplay(membership.interval)
          : '';
      case MembershipPurchaseType.prepaid:
        return membership.has_fixed_date_ranges
          ? 'Fixed Date Range'
          : getMembershipDurationDisplay(
              membership.duration_years,
              membership.duration_months,
              membership.duration_days,
            );
      case MembershipPurchaseType.benefit:
        return membership.start_date && membership.last_active_date
          ? `${dateStringFromISO8601(membership.start_date)} to the end of ${dateStringFromISO8601(membership.last_active_date)}`
          : 'Duration of Event';
      default:
        return '';
    }
  }

  // Whether this membership has an upcoming bill (recurring, active, started,
  // and not freezing or ending before the next bill date). The next bill date
  // is the membership's end date, as in the sub-list's status line.
  public get showNextBill(): boolean {
    const membership = this.membership;
    if (membership.purchase_type !== MembershipPurchaseType.recurring) {
      return false;
    }
    if (membership.status !== CustomerMembershipStatus.active) {
      return false;
    }
    if (!membership.has_started) {
      return false;
    }
    if (membership.membership_freeze_date) {
      return false;
    }
    // Cancelled on or before the next bill date: it ends first, no next bill.
    if (membership.membership_cancel_date
        && (membership.membership_cancel_date <= membership.end_date)) {
      return false;
    }
    return true;
  }

  // Prepaid memberships expire rather than bill, so they show an end date in
  // the slot where recurring memberships show their next bill date.
  public get showEndDate(): boolean {
    return (this.membership.purchase_type === MembershipPurchaseType.prepaid)
      && !!this.membership.end_date;
  }

  // Whether the customer used one of the membership's upfront payment options
  // at purchase. The purchase flow records nothing (rather than 1) when the
  // option is declined, so presence of the count is the whole test.
  public get paidUpfront(): boolean {
    return propExists(this.membership.number_of_upfront_periods_paid);
  }

  public get hasRecurringDiscount(): boolean {
    return !!this.membership.recurring_discount_amount
      && (parseFloat(this.membership.recurring_discount_amount) > 0);
  }

  public get hasOneOffDiscount(): boolean {
    return !!this.membership.one_off_discount_amount
      && (parseFloat(this.membership.one_off_discount_amount) > 0);
  }

  // The human-readable name/source of the recurring discount (code, association
  // and/or membership). Mirrors getRecurringDiscountNameString in the sub-list.
  public get recurringDiscountName(): string {
    const membership = this.membership;
    let name = membership.recurring_discount_name
      || membership.recurring_discount_code
      || '';

    if (membership.recurring_discount_association_name) {
      if (name) {
        name += ', ';
      }
      name += 'from ' + membership.recurring_discount_association_name;
      if (membership.recurring_discount_membership_name) {
        name += ' and ' + membership.recurring_discount_membership_name;
      }
    } else if (membership.recurring_discount_membership_name) {
      if (name) {
        name += ', ';
      }
      name += 'from ' + membership.recurring_discount_membership_name;
    }

    return name;
  }

  public get terms() {
    return this.membership.terms_and_conditions_agreed;
  }

  // The membership's members (linked and not-yet-linked), each with any extra
  // per-member detail lines. Add-ons are keyed by the customer id, or by
  // 'u'+id for not-yet-linked customers (mirrors the memberships sub-list).
  public get members(): MembershipDetailsMember[] {
    const linked = this.membership.all_customers.map(customer => ({
      id: customer.id,
      name: `${customer.first_name} ${customer.last_name}`,
      isUnlinked: false,
      details: this.memberDetails(customer.id.toString(), customer),
    }));
    const unlinked = this.membership.unlinked_customers.map(customer => ({
      id: customer.id,
      name: `${customer.first_name} ${customer.last_name}`,
      isUnlinked: true,
      details: this.memberDetails('u' + customer.id.toString(), null),
    }));
    return linked.concat(unlinked);
  }

  private memberDetails(
    addOnsKey: string,
    customer: CustomerMembershipCustomer | null,
  ): string[] {
    const details: string[] = [];

    const addOns = this.addOnsForKey(addOnsKey);
    if (addOns.length > 0) {
      details.push('Add-ons: ' + addOns.map(addOn => addOn.name).join(', '));
    }

    if (customer) {
      if (customer.is_individually_frozen) {
        details.push('Individually frozen');
      }
      if (customer.member_freeze_date) {
        details.push(
          'Will freeze on ' + dateStringFromISO8601(customer.member_freeze_date)
        );
      }
      if (customer.member_unfreeze_date) {
        details.push(
          'Will unfreeze on '
          + dateStringFromISO8601(customer.member_unfreeze_date)
        );
      }
      if (customer.is_pending_addition) {
        details.push('Will be added on next bill date');
      }
      if (customer.is_pending_removal) {
        details.push('Will be removed on next bill date');
      }
    }

    return details;
  }

  private addOnsForKey(key: string): MembershipPerCustomerAddOn[] {
    const addOns = this.membership.customer_add_ons[key];
    return (addOns && addOns.length) ? addOns : [];
  }

  // When the membership was initially purchased. Falls back to the first
  // completed invoice date when the precise purchase timestamp is unavailable
  // (e.g. some older memberships).
  public get purchasedDate(): string | null {
    return this.membership.purchase_completed_at
      || this.membership.first_completed_invoice_date;
  }

  public get hasRefund(): boolean {
    return parseFloat(this.membership.total_refunded_to_date) > 0;
  }

  public get termsAreTruncatable(): boolean {
    return !!this.terms.text
      && (this.terms.text.length > TERMS_TRUNCATE_LENGTH);
  }

  public get displayedTermsText(): string {
    if (this.showFullTerms || !this.termsAreTruncatable) {
      return this.terms.text;
    }
    return this.terms.text.slice(0, TERMS_TRUNCATE_LENGTH).replace(/\s+$/, '') + '…';
  }

  // The terms & conditions are stored as markdown (the same text customers see
  // when they agree), so render them rather than showing the raw source.
  public get renderedTermsHtml() {
    return this.markdownRender.getRenderedMarkdown(this.displayedTermsText);
  }

  // Close this modal, then open the manage/edit flow (same as the sub-list's
  // Edit / the roster report's Manage).
  public handleManage() {
    this.activeModal.close();
    this.manageCallback();
  }
}
